import { ReactNode } from 'react';
import merge from 'lodash/merge';
import styled, { css, CSSObject, CSSProperties } from 'styled-components';
import { breakpoint, BreakpointProp } from '../../utils/styles/breakpoints';
import { spacing } from '../../utils/styles/spacing';
import { DataTestId } from './DataTestIdWrapper';

const boxProps: readonly string[] = [
    'disableScrollbar',
    'display',
    'cursor',
    'pointerEvents',
    'transition',
    'filter',
    'backdropFilter',
    'boxSizing',
    'boxShadow',
    'position',
    'zIndex',
    'outline',
    'overflow',
    'overflowX',
    'overflowY',
    'height',
    'maxHeight',
    'minHeight',
    'width',
    'maxWidth',
    'minWidth',
    'margin',
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
    'm',
    'mt',
    'mr',
    'mb',
    'ml',
    'mx',
    'my',
    'padding',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'p',
    'pt',
    'pr',
    'pb',
    'pl',
    'px',
    'py',
    'alignSelf',
    'justifySelf',
    'borderRadius',
    'borderTopLeftRadius',
    'borderBottomLeftRadius',
    'borderTopRightRadius',
    'borderBottomRightRadius',
    'border',
    'borderTop',
    'borderRight',
    'borderBottom',
    'borderLeft',
    'b',
    'bt',
    'br',
    'bb',
    'bl',
    'background',
    'backgroundColor',
    'backgroundSize',
    'bg',
    'textDecoration',
    'textTransform',
    'textAlign',
    'top',
    'left',
    'right',
    'bottom',
    'transform',
    'transformOrigin',
    'opacity',
    'scrollSnapAlign',
    'scrollSnapType',
    'scrollSnapStop',
    'dataTestId',
    'scrollbarGutter',
];

export interface BoxProps {
    disableScrollbar?: boolean;
    boxSizing?: BreakpointProp<CSSProperties['boxSizing']>;
    cursor?: BreakpointProp<CSSProperties['cursor']>;
    pointerEvents?: BreakpointProp<CSSProperties['pointerEvents']>;
    transition?: BreakpointProp<CSSProperties['transition']>;
    boxShadow?: BreakpointProp<CSSProperties['boxShadow']>;
    filter?: BreakpointProp<CSSProperties['filter']>;
    fontSize?: BreakpointProp<CSSProperties['fontSize']>;
    alignSelf?: BreakpointProp<CSSProperties['alignSelf']>;
    justifySelf?: BreakpointProp<CSSProperties['justifySelf']>;
    backdropFilter?: BreakpointProp<CSSProperties['backdropFilter']>;
    display?: BreakpointProp<CSSProperties['display']>;
    visibility?: BreakpointProp<CSSProperties['visibility']>;
    position?: BreakpointProp<CSSProperties['position']>;
    zIndex?: BreakpointProp<CSSProperties['zIndex']>;
    overflow?: BreakpointProp<CSSProperties['overflow']>;
    overflowX?: BreakpointProp<CSSProperties['overflowX']>;
    overflowY?: BreakpointProp<CSSProperties['overflowY']>;
    width?: BreakpointProp<CSSProperties['width']>;
    maxWidth?: BreakpointProp<CSSProperties['maxWidth']>;
    minWidth?: BreakpointProp<CSSProperties['minWidth']>;
    height?: BreakpointProp<CSSProperties['height']>;
    maxHeight?: BreakpointProp<CSSProperties['maxHeight']>;
    minHeight?: BreakpointProp<CSSProperties['minHeight']>;
    margin?: BreakpointProp<CSSProperties['margin'] | number>;
    marginTop?: BreakpointProp<CSSProperties['marginTop'] | number>;
    marginRight?: BreakpointProp<CSSProperties['marginRight'] | number>;
    marginBottom?: BreakpointProp<CSSProperties['marginBottom'] | number>;
    marginLeft?: BreakpointProp<CSSProperties['marginLeft'] | number>;
    m?: BreakpointProp<CSSProperties['margin'] | number>;
    mt?: BreakpointProp<CSSProperties['marginTop'] | number>;
    mr?: BreakpointProp<CSSProperties['marginRight'] | number>;
    mb?: BreakpointProp<CSSProperties['marginBottom'] | number>;
    ml?: BreakpointProp<CSSProperties['marginLeft'] | number>;
    mx?: BreakpointProp<CSSProperties['marginLeft'] | number>;
    my?: BreakpointProp<CSSProperties['marginTop'] | number>;
    padding?: BreakpointProp<CSSProperties['padding'] | number>;
    paddingTop?: BreakpointProp<CSSProperties['paddingTop'] | number>;
    paddingRight?: BreakpointProp<CSSProperties['paddingRight'] | number>;
    paddingBottom?: BreakpointProp<CSSProperties['paddingBottom'] | number>;
    paddingLeft?: BreakpointProp<CSSProperties['paddingLeft'] | number>;
    p?: BreakpointProp<CSSProperties['padding'] | number>;
    pt?: BreakpointProp<CSSProperties['paddingTop'] | number>;
    pr?: BreakpointProp<CSSProperties['paddingRight'] | number>;
    pb?: BreakpointProp<CSSProperties['paddingBottom'] | number>;
    pl?: BreakpointProp<CSSProperties['paddingLeft'] | number>;
    px?: BreakpointProp<CSSProperties['paddingLeft'] | number>;
    py?: BreakpointProp<CSSProperties['paddingTop'] | number>;
    backgroundColor?: BreakpointProp<CSSProperties['backgroundColor']>;
    borderRadius?: BreakpointProp<CSSProperties['borderRadius'] | number>;
    borderTopLeftRadius?: BreakpointProp<CSSProperties['borderTopLeftRadius'] | number>;
    borderBottomLeftRadius?: BreakpointProp<CSSProperties['borderBottomLeftRadius'] | number>;
    borderTopRightRadius?: BreakpointProp<CSSProperties['borderTopRightRadius'] | number>;
    borderBottomRightRadius?: BreakpointProp<CSSProperties['borderBottomRightRadius'] | number>;
    border?: BreakpointProp<CSSProperties['border']>;
    borderTop?: BreakpointProp<CSSProperties['borderTop']>;
    borderRight?: BreakpointProp<CSSProperties['borderRight']>;
    borderBottom?: BreakpointProp<CSSProperties['borderBottom']>;
    borderLeft?: BreakpointProp<CSSProperties['borderLeft']>;
    b?: BreakpointProp<CSSProperties['border']>;
    bt?: BreakpointProp<CSSProperties['borderTop']>;
    br?: BreakpointProp<CSSProperties['borderRight']>;
    bb?: BreakpointProp<CSSProperties['borderBottom']>;
    bl?: BreakpointProp<CSSProperties['borderLeft']>;
    background?: BreakpointProp<CSSProperties['background']>;
    backgroundSize?: BreakpointProp<CSSProperties['backgroundSize']>;
    textAlign?: BreakpointProp<CSSProperties['textAlign']>;
    textDecoration?: BreakpointProp<CSSProperties['textDecoration']>;
    textTransform?: BreakpointProp<CSSProperties['textTransform']>;
    top?: BreakpointProp<CSSProperties['top']>;
    left?: BreakpointProp<CSSProperties['left']>;
    right?: BreakpointProp<CSSProperties['right']>;
    bottom?: BreakpointProp<CSSProperties['bottom']>;
    transform?: BreakpointProp<CSSProperties['transform']>;
    transformOrigin?: BreakpointProp<CSSProperties['transformOrigin']>;
    opacity?: BreakpointProp<CSSProperties['opacity']>;
    children?: ReactNode;
    scrollSnapAlign?: BreakpointProp<CSSProperties['scrollSnapAlign']>;
    scrollSnapType?: CSSProperties['scrollSnapType'];
    scrollSnapStop?: CSSProperties['scrollSnapStop'];
    wordBreak?: CSSProperties['wordBreak'];
    verticalAlign?: CSSProperties['verticalAlign'];
    dataTestId?: DataTestId;
    letterSpacing?: CSSProperties['letterSpacing'];
    scrollbarGutter?: BreakpointProp<CSSProperties['scrollbarGutter']>;
    animationDuration?: BreakpointProp<CSSProperties['animationDuration']>;
    userSelect?: BreakpointProp<CSSProperties['userSelect']>;
}

const boxModelStyles = (props: BoxProps): CSSObject =>
    merge(
        breakpoint('position', props.position),

        breakpoint('top', props.top, spacing),
        breakpoint('left', props.left, spacing),
        breakpoint('bottom', props.bottom, spacing),
        breakpoint('right', props.right, spacing),

        breakpoint('filter', props.filter),
        breakpoint('fontSize', props.fontSize),
        // @ts-ignore
        breakpoint('-webkit-backdrop-filter', props.backdropFilter),
        breakpoint('backdropFilter', props.backdropFilter),
        breakpoint('alignSelf', props.alignSelf),
        breakpoint('justifySelf', props.justifySelf),
        breakpoint('transform', props.transform),
        breakpoint('transformOrigin', props.transformOrigin),

        breakpoint('background', props.background),
        breakpoint('backgroundSize', props.backgroundSize),
        breakpoint('display', props.display),
        breakpoint('visibility', props.visibility),
        breakpoint('boxSizing', props.boxSizing),
        breakpoint('zIndex', props.zIndex),
        breakpoint('overflow', props.overflow),
        breakpoint('overflowX', props.overflowX),
        breakpoint('overflowY', props.overflowY),

        breakpoint('width', props.width),
        breakpoint('minWidth', props.minWidth),
        breakpoint('maxWidth', props.maxWidth),

        breakpoint('height', props.height),
        breakpoint('minHeight', props.minHeight),
        breakpoint('maxHeight', props.maxHeight),

        breakpoint('margin', props.margin ?? props.m, spacing),
        breakpoint('marginTop', props.marginTop ?? props.mt ?? props.my, spacing),
        breakpoint('marginRight', props.marginRight ?? props.mr ?? props.mx, spacing),
        breakpoint('marginBottom', props.marginBottom ?? props.mb ?? props.my, spacing),
        breakpoint('marginLeft', props.marginLeft ?? props.ml ?? props.mx, spacing),

        breakpoint('padding', props.padding ?? props.p, spacing),
        breakpoint('paddingTop', props.paddingTop ?? props.pt ?? props.py, spacing),
        breakpoint('paddingRight', props.paddingLeft ?? props.pr ?? props.px, spacing),
        breakpoint('paddingBottom', props.paddingBottom ?? props.pb ?? props.py, spacing),
        breakpoint('paddingLeft', props.paddingLeft ?? props.pl ?? props.px, spacing),

        breakpoint('borderRadius', props.borderRadius, spacing),
        breakpoint('borderTopLeftRadius', props.borderTopLeftRadius, spacing),
        breakpoint('borderBottomLeftRadius', props.borderBottomLeftRadius, spacing),
        breakpoint('borderTopRightRadius', props.borderTopRightRadius, spacing),
        breakpoint('borderBottomRightRadius', props.borderBottomRightRadius, spacing),

        breakpoint('border', props.border ?? props.b),
        breakpoint('borderTop', props.borderTop ?? props.bt),
        breakpoint('borderRight', props.borderRight ?? props.br),
        breakpoint('borderBottom', props.borderBottom ?? props.bb),
        breakpoint('borderLeft', props.borderLeft ?? props.bl),

        breakpoint('backgroundColor', props.backgroundColor),
        breakpoint('boxShadow', props.boxShadow),

        breakpoint('textTransform', props.textTransform),
        breakpoint('textDecoration', props.textDecoration),
        breakpoint('textAlign', props.textAlign),

        breakpoint('opacity', props.opacity),
        breakpoint('cursor', props.cursor),
        breakpoint('pointerEvents', props.pointerEvents),
        breakpoint('transition', props.transition),

        breakpoint('scrollSnapAlign', props.scrollSnapAlign),
        breakpoint('scrollSnapType', props.scrollSnapType),
        breakpoint('scrollSnapStop', props.scrollSnapStop),
        breakpoint('zIndex', props.zIndex),

        breakpoint('verticalAlign', props.verticalAlign),
        breakpoint('scrollbarGutter', props.scrollbarGutter),

        breakpoint('animationDuration', props.animationDuration),
        breakpoint('userSelect', props.userSelect),
    );

export const Box = styled('div')
    .attrs<BoxProps, BoxProps>(({ dataTestId, ...props }) => ({
        ...props,
        'data-test-id': dataTestId,
    }))
    .withConfig({
        displayName: 'Box',
        shouldForwardProp: (prop) => boxProps.indexOf(prop) === -1,
    })<BoxProps>`
    ${(props: BoxProps) =>
        props.disableScrollbar &&
        css`
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        `};

    ${css<BoxProps>(boxModelStyles)}
`;
